import { Box } from "@mui/material";
import { alpha } from "@mui/material/styles";
import { type CSSProperties, type ReactNode, useEffect, useRef, useState } from "react";
import type { RowComponentProps } from "react-window";
import { List, useListRef } from "react-window";
import { SURFACE } from "@/theme/tokens";

/** One rendered entry: its measured height and a render fn that receives the react-window style. */
export type VirtualEntry = {
	key: string;
	/** Option id for actionable rows (used to scroll the keyboard highlight into view); absent on group headers. */
	id?: string;
	height: number;
	render: (style: CSSProperties) => ReactNode;
};

const LIST_PADDING = 4;

type RowProps = { entries: VirtualEntry[] };

const Row = ({ index, style, entries }: RowComponentProps<RowProps>) => <>{entries[index].render(style)}</>;

type Props = {
	entries: VirtualEntry[];
	maxHeight: number;
	/** Id of the keyboard-highlighted row; the viewport scrolls to keep it visible past the fold (L10). */
	scrollToId?: string | null;
};

/**
 * Variable-height virtualized list (react-window). Snaps the viewport to whole rows so the last
 * visible row never clips mid-height (polish #1), and fades the bottom edge when more rows exist.
 */
export const DropdownVirtualList = ({ entries, maxHeight, scrollToId }: Props) => {
	const [scrolledToEnd, setScrolledToEnd] = useState(false);
	const listRef = useListRef(null);

	// Read `entries` from a ref inside the scroll effect so the effect depends ONLY on `scrollToId`. `entries`
	// gets a fresh identity on every selection toggle (its useMemo keys on `effectiveIds`), so including it here
	// would re-fire the scroll on each mouse click and yank a scrolled list back to the first-row highlight.
	const entriesRef = useRef(entries);
	entriesRef.current = entries;

	// Keep the keyboard-highlighted row in view: react-window only mounts the viewport (+overscan), so without
	// this an ArrowDown past the fold moves an unmounted, invisible highlight. Fires only when the highlight moves.
	useEffect(() => {
		if (!scrollToId) return;
		const index = entriesRef.current.findIndex((entry) => entry.id === scrollToId);
		if (index >= 0) listRef.current?.scrollToRow({ index, align: "auto" });
	}, [scrollToId, listRef]);

	// Total scroll height = every row plus the top+bottom list padding (both are part of the List's
	// scrollable content, so both must be counted or the viewport comes up short and phantom-scrolls).
	const totalHeight = entries.reduce((sum, entry) => sum + entry.height, 0) + LIST_PADDING * 2;
	let viewportHeight: number;
	if (totalHeight <= maxHeight) {
		// Everything fits — the viewport is exactly the content, so there is no scrollbar.
		viewportHeight = totalHeight;
	} else {
		// Overflowing: snap the viewport to a whole number of rows (never cut a row mid-height),
		// counting both paddings, and always keep at least the first row visible.
		let used = LIST_PADDING * 2;
		for (const entry of entries) {
			if (used + entry.height > maxHeight) break;
			used += entry.height;
		}
		viewportHeight = Math.max(used, (entries[0]?.height ?? 0) + LIST_PADDING * 2);
	}
	const overflowing = totalHeight > viewportHeight + 1;

	return (
		<Box sx={{ position: "relative" }}>
			<List
				listRef={listRef}
				rowProps={{ entries }}
				rowComponent={Row}
				rowCount={entries.length}
				rowHeight={(index: number, { entries: rows }: RowProps) => rows[index].height}
				defaultHeight={viewportHeight}
				overscanCount={6}
				onRowsRendered={({ stopIndex }) => setScrolledToEnd(stopIndex >= entries.length - 1)}
				style={{ height: viewportHeight, paddingBlock: LIST_PADDING, overflowX: "clip", scrollbarWidth: "thin" }}
			/>
			{overflowing && !scrolledToEnd && (
				<Box
					aria-hidden
					sx={{
						position: "absolute",
						left: 0,
						right: 0,
						bottom: 0,
						height: 24,
						pointerEvents: "none",
						background: `linear-gradient(to top, ${SURFACE}, ${alpha(SURFACE, 0)})`,
					}}
				/>
			)}
		</Box>
	);
};
